<template>
    <div>
        <div class="loader__wrapper loader__wrapper--loading loader__wrapper--xl" v-if="!vm">
            <div class="loader">
                <div class="loader__circle"></div>
                <div class="loader__text">{{labels.loading}}</div>
            </div>
        </div>
        <div v-if="vm" :class="{'form--locked': !vm.canEdit }">
            <div class="grid-container">
                
                <!-- Title and Intro -->
                <div class="grid-x">
                    <div class="head head--blue cell">
                        <div class="head__content">
                            <a tabindex="0" @click="$router.go(-1)" @keyup.enter="$router.go(-1)" class="head__back"><span class="icon-arrow-left"></span>{{labels.back}}</a>
                            <h1 class="head__title" v-if="mode === 'SuggestStudy'">{{labels.title}}</h1>
                            <h1 class="head__title" v-else>{{title}}</h1>
                        </div>
                    </div>
                    <div class="form--edit-study content content--gray form__divider cell" v-if="mode === 'SuggestStudy'">
                        <p class="lead" v-html="labels.intro"></p>
                    </div>
                </div>

                <!-- Form: title, author, year etc. -->
                <div class="grid-x">
                    <div class="form--edit-study content content--gray form__divider cell">
                        <div class="grid-x">
                            <div class="form__item large-6 cell">
                                <textarea rows="4" 
                                    :disabled="!vm.canEdit"
                                    name="Studietitel" 
                                    id="studyTitle" v-floatable 
                                    v-model="vm.title"
                                     :data-vv-as="labels.studyTitle"   
                                    :class="{'input--invalid': showInvalid('Studietitel') }"
                                    v-validate.initial="'required'" maxlength="500">
                                </textarea>
                                <label for="studyTitle" class="floating-label">{{labels.studyTitle}}* {{ counter(vm.title.length, 500) }}</label>
                            </div>    
                            <div class="form__item large-6 cell">
                                <textarea rows="4" 
                                    :disabled="!vm.canEdit"
                                    name="Forfattere" 
                                    id="authors" v-floatable 
                                    v-model="vm.authors"
                                    :data-vv-as="labels.authors"  
                                    :class="{'input--invalid': showInvalid('Forfattere') }"
                                    v-validate.initial="'required'" maxlength="500">
                                </textarea>
                                <label for="authors" class="floating-label">{{labels.authors}}* {{ counter(vm.authors.length, 500) }}</label>
                            </div>
                        </div>
                        <div class="grid-x">
                            <div class="form__item large-6 cell">
                                <select name="Udgivelsesår" 
                                    :disabled="!vm.canEdit"
                                    id="pubYear" 
                                    class="form__select" 
                                    v-model="vm.publicationYear.selected" 
                                    v-validate.initial="'required'"
                                      :data-vv-as="labels.publicationYear"  
                                    :class="{'input--invalid': showInvalid('Udgivelsesår') }" 
                                    v-floatable>
                                    <option disabled selected :value="null">{{labels.choosePublicationYear}}*</option>
                                    <option v-for="option in vm.publicationYear.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                                </select>
                                <label for="pubYear" class="floating-label show-for-sr">{{labels.publicationYear}}*</label>
                            </div>
                        </div>
                            
                        <journals-autocomplete 
                            :editable="vm.canEdit" 
                            :publishedInScientificJournal="vm.publishedInScientificJournal" 
                            :publicationPlace="vm.publicationPlace" 
                            :publicationComment="vm.publicationComment"
                            :scientificJournalId="vm.scientificJournalId"
                            @update="updateJournal">
                        </journals-autocomplete>

                    </div>
                </div>

                <!-- Form: country, quality etc. AND resumé input fields -->
                <div class="grid-x">
                    <div class="form--edit-study content content--gray cell">
                            <h2 class="h1 text-center">{{labels.weights}}</h2>
                            <div class="grid-x">
                                <div class="form__item large-4 cell">
                                    <select name="Land" 
                                        :disabled="!vm.canEdit"
                                        id="country" 
                                        class="form__select" 
                                        v-model="vm.country.selected" 
                                        v-validate.initial="'required'" 
                                        :class="{'input--invalid': showInvalid('Land') }"
                                        :data-vv-as="labels.country" 
                                        v-floatable>
                                        <option disabled selected :value="null">{{labels.chooseCountry}}*</option>
                                        <option v-for="option in vm.country.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                    <label for="country" class="floating-label">{{labels.country}}*</label>
                                </div>
                                <div class="form__item large-4 cell">
                                    <select name="Analysemetode" 
                                        :disabled="!vm.canEdit"
                                        id="studyType" 
                                        class="form__select" 
                                        v-model="vm.studyType.selected" 
                                        v-validate.initial="'required'" 
                                        :class="{'input--invalid': showInvalid('Analysemetode') }"
                                        :data-vv-as="labels.studyAssessment" 
                                        v-floatable>
                                        <option disabled selected :value="null">{{labels.chooseStudyAssessment}}*</option>
                                        <option v-for="option in vm.studyType.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                    <label for="studyType" class="floating-label">{{labels.studyAssessment}}*</label>
                                </div>
                                <div class="form__item large-4 cell">
                                    <select name="Studiets kvalitet" 
                                        :disabled="!vm.canEdit"
                                        id="studyAssessment" 
                                        class="form__select" 
                                        v-model="vm.studyAssessment.selected" 
                                        v-validate.initial="'required'"
                                        :data-vv-as="labels.studyQuality"  
                                        :class="{'input--invalid': showInvalid('Studiets kvalitet') }"
                                        v-floatable>
                                        <option disabled selected :value="null">{{labels.chooseStudyQuality}}*</option>
                                        <option v-for="option in vm.studyAssessment.options" :key="option.id" :value="option.id">{{ option.name }}</option>
                                    </select>
                                    <label for="studyAssessment" class="floating-label">{{labels.studyQuality}}*</label>
                                </div>
                            </div>

                            <h2 class="h1 text-center">{{labels.studySummary}}</h2>
                            <div class="grid-x">
                                <div class="form__language-column large-6 cell">
                                    <h3>{{labels.danish}}</h3>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="what" 
                                            id="what" v-floatable 
                                            v-model="vm.examinesWhat[1].text" 
                                            :class="{'input--invalid': showInvalid('what') }"
                                            :data-vv-as="labels.examinesWhat"
                                            v-validate.initial="'required'" maxlength="1500">
                                        </textarea>
                                        <label for="what" class="floating-label">{{labels.examinesWhat}}* {{ counter(vm.examinesWhat[1].text.length, 1500) }} <small>{{labels.examinesWhatDescription}}</small></label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="how" 
                                            id="how" 
                                            v-model="vm.examinesHow[1].text" 
                                            v-validate.initial="'required'" maxlength="1500" 
                                            :class="{'input--invalid': showInvalid('how') }"
                                            :data-vv-as="labels.examinesHow"
                                            v-floatable>
                                        </textarea>
                                        <label for="how" class="floating-label">{{labels.examinesHow}}* {{ counter(vm.examinesHow[1].text.length, 1500) }} <small>{{labels.examinesHowDescription}}</small></label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="findings" 
                                            id="findings" 
                                            v-model="vm.findings[1].text" 
                                            v-validate.initial="'required'" maxlength="1500" 
                                            :class="{'input--invalid': showInvalid('findings') }"
                                            :data-vv-as="labels.findings"
                                            v-floatable>
                                        </textarea>
                                        <label for="findings" class="floating-label">{{labels.findings}}* {{ counter(vm.findings[1].text.length, 1500) }}</label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="challanges" 
                                            id="challanges" 
                                            v-model="vm.challenges[1].text" 
                                            v-validate.initial="'required'" maxlength="1500" 
                                            :class="{'input--invalid': showInvalid('challanges') }"
                                            :data-vv-as="labels.challenges"
                                            v-floatable>
                                        </textarea>
                                        <label for="challanges" class="floating-label">{{labels.challenges}}* {{ counter(vm.challenges[1].text.length, 1500) }}</label>
                                    </div>
                                    
                                </div>
                                <div class="form__language-column large-6 cell">
                                    <h3>{{labels.english}}</h3>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="what_en" 
                                            id="what_en" 
                                            v-floatable 
                                            v-model="vm.examinesWhat[0].text" 
                                            :class="{'input--invalid': showInvalid('what_en') }"
                                            :data-vv-as="labels.examinesWhat"
                                            v-validate.initial="'required'" maxlength="1500" spellcheck="false">
                                        </textarea>
                                        <label for="what_en" class="floating-label">{{labels.examinesWhat}}* {{ counter(vm.examinesWhat[0].text.length, 1500) }}<small>{{labels.examinesWhatDescription}}</small></label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="how_en" 
                                            id="how_en" 
                                            v-model="vm.examinesHow[0].text" 
                                            v-floatable 
                                            :class="{'input--invalid': showInvalid('how_en') }"
                                            :data-vv-as="labels.examinesHow"
                                            v-validate.initial="'required'" maxlength="1500" spellcheck="false">
                                        </textarea>
                                        <label for="how_en" class="floating-label">{{labels.examinesHow}}* {{ counter(vm.examinesHow[0].text.length, 1500) }}<small>{{labels.examinesHowDescription}}</small></label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="findings_en" 
                                            id="findings_en" 
                                            v-model="vm.findings[0].text" 
                                            v-validate.initial="'required'" maxlength="1500" 
                                            :class="{'input--invalid': showInvalid('findings_en') }"
                                            :data-vv-as="labels.findings"
                                            v-floatable spellcheck="false">
                                        </textarea>
                                        <label for="findings_en" class="floating-label">{{labels.findings}}* {{ counter(vm.findings[0].text.length, 1500) }}</label>
                                    </div>

                                    <div class="form__item">
                                        <textarea rows="6" 
                                            :disabled="!vm.canEdit"
                                            name="challanges_en" 
                                            id="challanges_en" 
                                            v-model="vm.challenges[0].text" 
                                            v-validate.initial="'required'" maxlength="1500" 
                                            :class="{'input--invalid': showInvalid('challanges_en') }"
                                            :data-vv-as="labels.challenges"
                                            v-floatable spellcheck="false">
                                        </textarea>
                                        <label for="challanges_en" class="floating-label">{{labels.challenges}}* {{ counter(vm.challenges[0].text.length, 1500) }}</label>
                                    </div>
                                </div>
                            </div>

                    </div>
                </div>

                <!-- Link to study file or url -->
                <div class="grid-x" v-if="vm.url || studySource">
                    <div class="form--edit-study content content--gray cell">
                        <p v-if="vm.url">
                            <span>{{labels.linkToStudy}} </span><br />
                            <a tabindex="0" :href="vm.url" target="_blank" class="text-bold">{{vm.url}}</a>
                        </p>
                        <p v-else-if="studySource && studySource.hasFile" class="buttons buttons--external">
                            <span class="text--high">{{labels.downloadStudyPdf}} </span>
                            <button @click="download" @keyup.enter="download" class="button">{{ studySource.fileName }}</button>
                        </p>
                    </div>
                </div>

                <!-- Form: upload or url -->
                <div class="grid-x" v-if="mode === 'SuggestStudy'">
                    <div class="content content--gray cell">
                        <div class="grid-x">
                            <div class="form__item cell">
                                <label class="label--radio">
                                    <input type="radio" name="Fil til studiet" value="file" v-model="suggest.source" v-validate.initial="'required'" />
                                    <span>{{labels.iHaveAPdfFile}}*</span>
                                </label>
                                <label class="label--radio">
                                    <input type="radio" name="- Eller link til studiet" value="url" v-model="suggest.source" v-validate.initial="'required'" />
                                    <span>{{labels.iHaveALink}}*</span>
                                </label>
                                <br />
                            </div>
                        </div>
                    </div>

                    <div class="form__file-section content--light-blue cell">
                        <div class="grid-x" v-if="suggest.source === 'file'">
                            <div class="form__item  text-center text-right-large large-3 cell">
                                <br />
                                <b class="text--high">{{labels.attachTheStudy}}</b>
                            </div>
                            <div class="form__item large-6 cell">
                                <label for="studyFileUpload" class="form__file-label">
                                    <span class="form__file-button">{{labels.choosePdfFile}}</span>
                                    <span class="form__file-name">{{fileName}}</span>
                                </label>
                                <input type="file" ref="studyFile" name="Studie PDF-fil" accept="application/pdf" id="studyFileUpload" class="show-for-sr" @change="fileChanged" v-validate.initial="'required'">
                            </div>
                        </div>
                        <div class="grid-x" v-if="suggest.source === 'url'" style="padding: 25px 0px 0px 65px;">
                            <div class="form__item large-6 cell">
                                <input type="url" name="Link til studie" id="link" v-model="vm.url" v-floatable v-validate.initial="'required'">
                                <label for="link" class="floating-label">{{labels.urlToStudy}}</label>
                            </div>
                        </div>
                    </div>
                    <p>&nbsp;</p>
                </div>

                <!-- Form: results -->
                <div class="grid-x" v-if="(vm.canEdit === true || vm.studyResults.length > 0) && mode !== 'SuggestStudy'">
                    <div class="cell form--edit-study content content--gray">
                        <h2 class="h1 text-center">{{labels.results}}</h2>
                            <ul class="accordion accordion--edit-study">
                                <li class="accordion__item" 
                                    v-for="result of vm.studyResults" 
                                    :key="result.id" 
                                    :class="{ 'accordion__item--open': activeResult === result.id }"
                                    :id="result.id">
                                    
                                    <a tabindex="0" @click="toggleResult(result.id)" @keyup.enter="toggleResult(result.id)" class="accordion__title">
                                        <b>{{ result.title || 'Nyt resultat' }}</b>
                                        <span class="accordion__plus icon-plus"></span>
                                        <span v-if="activeResult !== result.id" class="accordion__duplicate icon-duplicate" @click.stop="copyResult(result)" @keyup.stop ="copyResult(result)"></span>
                                    </a>
                                    <study-result v-if="activeResult === result.id" 
                                        :result.sync="result" 
                                        :canEdit="vm.canEdit" 
                                        @save="saveResult"
                                        @close="closeResult"></study-result>
                                </li>
                            </ul>

                        <p v-if="activeResult === null && vm.canEdit">
                            <button @click="newResult()" @keyup.enter="newResult()"  class="button expanded" :disabled="resultLoading">{{labels.enterNewResult}}</button>
                        </p>
                        <div v-else-if="activeResult !== null && vm.canEdit" class="accordion--overlay"></div>
                    </div>
                </div>

                <!-- Comments -->
                <comments v-if="commentsAllowed"></comments>
                
                <!-- Submit and validation -->
                <div class="grid-x grid-margin-x">
                    <div class="cell form__buttons">
                        <div class="grid-x grid-padding-x" v-if="vm.canEdit">
                            <div class="validation__summary cell" v-if="errors.items.length > 0 || errorScientificJournal">
                                <h2 class="h4 text--red">{{labels.theFollowingShouldBeCorrected}}</h2>
                                <ul>
                                    <li v-for="error in errors.items" :key="error.id">{{ error.msg }}</li>
                                    <li v-if="errorScientificJournal">{{labels.indicateWhichJournal}}</li>
                                </ul>
                            </div>
                            <div class="large-4 cell">
                                <div v-if="mode !== 'SuggestStudy'">
                                    <a tabindex="0" @click="saveDraft(false)"  @keyup.enter="saveDraft(false)"  class="text--high" :class="{'text--disabled': (!canSaveDraft || busy) }"><span class="icon-circle-right icon--large"></span> {{labels.saveDraft}}</a>
                                    <span class="label draft--msg" :class="{ 'draft--error': msg.error }"  v-if="msg.show">{{ msg.error ? "Error:" : "Success:" }} {{msg.text}}</span>                                    
                                </div>
                            </div>
                            <div class="large-4 cell">
                                <p>
                                    <button v-if="mode === 'SuggestStudy'" class="button expanded" :disabled="errors.items.length > 0 || errorScientificJournal || busy" @click="suggestSubmit" @keyup.enter="suggestSubmit">{{labels.submitStudy}}</button>
                                    <button v-else class="button expanded" :disabled="errors.items.length > 0 || errorScientificJournal|| busy" @click="saveDraft(true)" @keyup.enter="saveDraft(true)">{{labels.submitStudyForApproval}}</button>
                                </p>
                            </div>
                            <div class="large-4 cell">
                            </div>
                        </div>
                        <div v-else class="grid-x grid-padding-x">
                            <div class="cell">
                                <button class="button" @click="$router.go(-1)" @keyup.enter="$router.go(-1)">{{labels.back}}</button>
                            </div>
                        </div>

                        </div>
                    </div>
                </div>

                <!-- Modal: send to approval -->
                <modal name="approved-message" :adaptive="true" :width="500" :height="'auto'" :pivot-y=".25" :click-to-close="false" :focusTrap="true" class="modal modal--save">
                    <header class="modal__header">
                        <h2>{{labels.studyWasSubmitted}}</h2>
                    </header>
                    <div class="grid-x grid-padding-x">
                        <div class="modal__content cell">
                            <div>
                                <p>
                                    {{labels.studyWasSubmittedMessage}}
                                </p>
                            </div>
                        </div>
                    </div>
                    <footer class="form__divider form__links text-center">
                        <router-link to="/" class="button">{{labels.ok}}</router-link>
                    </footer>
                </modal>

                <!-- Modal: confirm suggest study -->
                <modal name="suggested-message" :adaptive="true" :width="500" :height="'auto'" :pivot-y=".25" :click-to-close="false" :focusTrap="true" class="modal modal--save">
                    <header class="modal__header">
                        <h2>{{labels.successTitle}}</h2>
                    </header>
                    <div class="grid-x grid-padding-x">
                        <div class="modal__content cell">
                            <div>
                                <p>
                                    {{labels.successText}}
                                </p>
                            </div>
                        </div>
                    </div>
                    <footer class="form__divider form__links text-center">
                        <router-link to="/MyStudies" class="button">{{labels.ok}}</router-link>
                    </footer>
                </modal>
            </div>
        </div>
</template>
<script>
import { remove, findIndex, some, cloneDeep } from "lodash";
import JournalsAutocomplete from '../JournalsAutocomplete'

export default {
    components: {
        JournalsAutocomplete
    },
    data() {
        return {
            loaded: true,
            editResult: false,
            activeResult: null,
            publishOnSave: false,
            submitted: false,
            commentsAllowed: this.$route.name !== "CreateStudy",
            fileName: 'Der er ikke valgt nogen fil',
            resultLoading: false,
            busy: false,
            errorScientificJournal: false,
            suggest: {
                source: null,
                comment: null
            },
            msg: {
                show: false,
                text: null,
                error: false,
                timer: null
            }
        };
    },
    computed: {
        vm() {
            return (this.mode === 'SuggestStudy') ? this.$store.state.suggestStudyVm : this.$store.state.createStudyVm;
        },
        studySource() {
            return this.$store.state.studySource;
        },
        mode() {
            return this.$route.name;
        },
        title() {
            var title;
            switch (this.mode) {
                case 'CreateStudy':
                    title = this.labels.createStudy;
                    break;
                // case 'SuggestStudy':
                //     title = 'Indsend nyt studie';
                //     break;
                default:
                    title = window.vm.canEdit ? this.labels.editStudy: this.labels.studyInformation;
            }
            return title;
        },
        labels() {
            return this.$store.state.labels["pages.mypage.suggestStudy"] || {};
        },
        canSaveDraft(){
            let any = some(this.errors.items, (item) => { 
                return ['Studietitel', 'Forfattere', 'Udgivelsesår', 'Land', 'Analysemetode', 'Studiets kvalitet'].indexOf(item.field) !== -1;
            });
            return !this.errorScientificJournal && !any;
        }
    },
    methods: {
        toggleResult(id) {
            this.activeResult = id;
            var elem = document.getElementById(id);
            if(elem !== null) setTimeout(() => { elem.focus(); elem.scrollIntoView({ behavior: 'smooth' }) }, 300);
        },
        closeResult(settings) {
            this.activeResult = null;
            if (settings.delete && settings.result !== undefined) {
                this.removeResult(settings.result);
            }
        },
        copyResult(originalResult){
            this.resultLoading = true;
            this.$store.dispatch("getCreateStudyResult").then(result => {
                let resultClone = cloneDeep(originalResult)
                resultClone.title += " (kopi)";
                resultClone.id = result.id;
                //this.$store.commit('CLONE_RESULT', resultClone);
                //result.isNew = true;
                this.vm.studyResults.push(resultClone);
                this.$store.commit("CREATESTUDYVM", this.vm);
                //this.toggleResult(result.id);
                this.resultLoading = false;
            });
        },
        saveResult(restultDraft) {
            var pos = findIndex(this.vm.studyResults, (o) => { return o.id === restultDraft.id });
            this.vm.studyResults[pos] = restultDraft;
            this.closeResult({ delete: false });
        },
        saveDraft(sendToApproval) {
            this.busy = true;
            this.$store.dispatch("saveStudy", this.vm).then(result => {
                
                if (result.success && sendToApproval) {
                    this.$store
                        .dispatch("sendToApproval", { studyId: this.vm.id })
                        .then(result => {
                            this.updateMsg(result);
                            this.$modal.show('approved-message');
                            this.busy = false;
                        });
                } else if (result.success) {
                    this.submitted = true;
                    this.commentsAllowed = true;
                    this.updateMsg(result);
                    this.busy = false;
                } else {
                    this.updateMsg(result);
                    this.busy = false;
                }
            });
        },
        updateMsg(result, error) {
            this.msg.text = result.succes ? result.message : result.message || "Der opstod en fejl og studiet blev ikke gemt.";
            this.msg.error = !result.success;
            this.msg.show = true;

            clearTimeout(this.msg.timer);
            this.msg.timer = setTimeout(() => {
                this.msg.show = false;
            }, 6000);
        },
        showInvalid(name) {
            return this.errors.has(name) && this.submitted;
        },
        newResult() {
            this.resultLoading = true;
            this.$store.dispatch("getCreateStudyResult").then(result => {
                result.isNew = true;
                this.vm.studyResults.push(result);
                this.$store.commit("CREATESTUDYVM", this.vm);
                this.toggleResult(result.id);
                this.resultLoading = false;
            });
        },
        removeResult(result) {
            remove(this.vm.studyResults, result);
            this.$store.commit("CREATESTUDYVM", this.vm);
        },
        suggestSubmit() {
            this.busy = true;
            var _data = {
                vm: this.vm,
                file: this.$refs.studyFile ? this.$refs.studyFile.files[0] : null
            }
            this.$store.dispatch("suggestStudy", _data).then(result => {
                this.busy = false;
                this.$modal.show('suggested-message');
            })
            .catch((error) => {
                console.warn(error);
            })
        },
        fileChanged() {
            this.fileName = this.$refs.studyFile.files[0] ? this.$refs.studyFile.files[0].name : 'Der er ikke valgt nogen fil';
        },
        download() {
            this.$store.dispatch('downloadStudy', { id: this.vm.id, name: this.studySource.fileName });
        },
        counter(count, maxCount){
            return (count === 0) ? "" : count + '/' + maxCount + ' tegn'; 
        },
        updateJournal(val){
            this.vm.publishedInScientificJournal = val.published; 
            this.vm.publicationPlace = val.place;
            this.vm.publicationComment = val.comment;
            this.vm.scientificJournalId = val.journalId;
            this.errorScientificJournal = val.error;
        }

    },
    mounted() {
        if (this.mode === 'SuggestStudy') {
            this.$store.dispatch("getSuggestStudyVm", { id: this.$route.query.id });
        } else {
            this.$store.dispatch("getCreateStudyVm", { id: this.$route.query.id });
        }
        this.$store.dispatch('getLabels', { label: 'pages.mypage.suggestStudy' });
    },
    created() {
        this.$store.commit('SUGGESTSTUDYVM', null);
        this.$store.commit('CREATESTUDYVM', null);
    }
};
</script>



// WEBPACK FOOTER //
// src/components/my-pages/EditStudy.vue